import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { QRCode } from 'react-qr-code';
import ThemeResultNew from '../../components/ThemeResultNew';
import Modal from '../../components/Modal';
import LoadingIndicator from '../../components/LoadingIndicator';
import useTrackEvent from '../../utils/trackEvent';
import { usePreferences } from '../../context/PreferencesContext';
import { decompressFromEncodedURIComponent } from 'lz-string';
import { buildThemeImageMap } from '../../utils/buildThemeImageMap';
import type { AdviceResponse } from '../../types/product';

interface CheckedAnswerEntry {
  subthemes?: Record<string, string>[];
}

function Results() {
  const { setResetQuestionnaire } = usePreferences();
  const trackEvent = useTrackEvent();
  const location = useLocation();
  const [results, setResults] = useState<AdviceResponse | null>(null);
  const [didExistanceCheck, setDidExistanceCheck] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const [shareLinkModalIsOpen, setShareLinkModalIsOpen] = useState(false);
  const [qrCodeModalIsOpen, setQRCodeModalIsOpen] = useState(false);
  const [linkIsCopied, setLinkIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkedAnswers, setCheckedAnswers] = useState<Record<string, CheckedAnswerEntry>>({});
  const [themeImageMap, setThemeImageMap] = useState<Record<string, string>>({});

  useEffect(() => {
    setThemeImageMap(buildThemeImageMap());
  }, []);

  const resetQuestionnaireState = () => { setResetQuestionnaire(true); };

  useEffect(() => {
    if (results === null || didExistanceCheck) return;
    setDidExistanceCheck(true);

    const newResults = { ...results };

    let checkProductApiUrl: string | false = import.meta.env.VITE_LIBRARY_ENDPOINT ?? '';
    if (window.customer) checkProductApiUrl = window.customer.library_endpoint ?? '';

    try {
      new URL(checkProductApiUrl as string);
    } catch {
      checkProductApiUrl = false;
    }

    const productsList = Object.values(newResults.products);

    if (checkProductApiUrl === false) {
      productsList.forEach((product) => {
        product.exists = false;
        trackEvent('Product_Advised', { select: product.name, id: product.id, uuid: product.uuid, exists_in_library: false });
      });
      setResults({ ...newResults });
    } else {
      axios
        .post(checkProductApiUrl + '/wp-json/wc-uvital/v1/products/exists', productsList.map((p) => p.uuid ?? ''))
        .then((response) => {
          if (response.data) {
            productsList.forEach((product) => {
              const productData = product.uuid ? (response.data[product.uuid] ?? false) : false;
              product.exists = productData;
              trackEvent('Product_Advised', { select: product.name, id: product.id, uuid: product.uuid, exists_in_library: productData });
            });
          }
        })
        .catch((error) => {
          productsList.forEach((product) => {
            product.exists = false;
            trackEvent('Product_Advised', { select: product.name, id: product.id, uuid: product.uuid, exists_in_library: false });
          });
          console.log(error);
        })
        .finally(() => { setResults({ ...newResults }); });
    }
  }, [results]);

  useEffect(() => {
    const getAdvice = async (data: unknown) => {
      setIsLoading(true);
      const baseUrl = import.meta.env.VITE_AZURE_API_URL;
      await axios
        .request({ method: 'post', url: baseUrl + '/api/v1/advice', headers: { 'Content-Type': 'application/json' }, data: JSON.stringify(data) })
        .then((response) => { if (response.data) setResults(response.data); })
        .catch((error) => { console.log(error); setError(error.message); })
        .finally(() => { setIsLoading(false); });
    };

    setShareableLink(window.location.href);

    const params = new URLSearchParams(location.search);
    let urlParams: URLSearchParams;
    const encodedParams = params.get('data');
    if (encodedParams) {
      const decodedParams = decompressFromEncodedURIComponent(encodedParams);
      urlParams = new URLSearchParams(decodedParams);
    } else {
      urlParams = new URLSearchParams(location.search);
    }

    const themes: { theme: string; advice: { products: string[]; content: string[] } }[] = [];
    let index = 0;
    while (urlParams.has(`theme${index}`)) {
      themes.push({
        theme: urlParams.get(`theme${index}`) ?? '',
        advice: {
          products: urlParams.get(`products${index}`)?.split(',') ?? [],
          content: urlParams.get(`content${index}`)?.split(',') ?? [],
        },
      });
      index++;
    }

    const groupedAnswersParam = urlParams.get('groupedAnswers');
    if (groupedAnswersParam) {
      setCheckedAnswers(JSON.parse(decodeURIComponent(groupedAnswersParam)));
    }

    if (themes.length > 0) getAdvice(themes);
  }, [location]);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(shareableLink);
    setLinkIsCopied(true);
    setTimeout(() => { setLinkIsCopied(false); }, 2000);
  };

  return (
    <section className="site-container">
      <div className="background-header-results">
        <div className="site-container">
          <Link to="/questions" className="inline-flex items-center gap-2.5">
            <button className="btn btn-outline-white">
              <i className="fas fa-chevron-left"></i>
              <span className="text-lg">Terug naar vragenlijst</span>
            </button>
          </Link>
          <h1 className="text-white mt-4 lg:mt-2.5">Jouw advies</h1>
          <p className="text-white mt-1 lg:mt-2.5">Hieronder vind je tips en producten die je kunnen helpen.</p>
        </div>
      </div>
      <div className="w-full rounded-lg">
        <div className="site-container">
          {isLoading && <div className="h-96 flex justify-center items-center"><LoadingIndicator /></div>}
          {results !== null && 'advice' in results && results.advice.length > 0 && !isLoading && !error && (
            <>
              {results.advice.map((theme) => (
                <ThemeResultNew key={uuidv4()} theme={theme} checkedAnswers={checkedAnswers} adviceResponseObject={results} themeImageMap={themeImageMap} />
              ))}
            </>
          )}
          <div className="flex flex-col gap-4 sm:p-10 sm:bg-white mt-6 lg:mt-5 rounded-[20px]">
            <h3>Advies bewaren</h3>
            <div className="flex flex-wrap justify-between items-center gap-2.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <button className="btn btn-secondary" onClick={window.print}>Opslaan als PDF</button>
                <button className="btn btn-secondary" onClick={() => setQRCodeModalIsOpen(!qrCodeModalIsOpen)}>QR code</button>
                <button className="btn btn-secondary" onClick={() => setShareLinkModalIsOpen(!shareLinkModalIsOpen)}>Delen</button>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-4 sm:p-10 sm:bg-white mt-6 lg:mt-5 rounded-[20px]">
            <h3>Doe de vragenlijst nog een keer</h3>
            <p className="text-lg mt-1">Dit advies is gemaakt op basis van jouw antwoorden. Je kunt de vragenlijst nog een keer invullen.</p>
            <Link to="/" onClick={resetQuestionnaireState} className="text-main underline font-semibold text-lg">
              Naar de startpagina
            </Link>
          </div>
        </div>
      </div>
      <Modal show={shareLinkModalIsOpen} handleClose={() => setShareLinkModalIsOpen(!shareLinkModalIsOpen)}>
        <div className="p-6">
          <h3>Advies delen</h3>
          <p className="p-6 bg-support-grey-100 rounded-lg mt-6 truncate">{shareableLink}</p>
          <div className="space-x-4 mt-4">
            <a href={`mailto:?subject=Mijn Thuisleefwijzer Resultaat&body=${encodeURIComponent(shareableLink)}`} className="btn btn-primary-outline">
              <i className="fas fa-envelope text-main"></i>
              Link mailen
            </a>
            {linkIsCopied ? (
              <button className="btn btn-primary-outline"><i className="fas fa-check text-main"></i>Link gekopieerd!</button>
            ) : (
              <button className="btn btn-primary-outline" onClick={handleCopyToClipboard}>
                <i className="fas fa-copy text-main"></i>Link kopiëren
              </button>
            )}
          </div>
        </div>
      </Modal>
      <Modal show={qrCodeModalIsOpen} handleClose={() => setQRCodeModalIsOpen(!qrCodeModalIsOpen)}>
        <div className="p-6">
          <h3>Scan de QR-code met de camera van je mobiele telefoon</h3>
          <div className="flex justify-center my-12"><QRCode value={shareableLink} /></div>
        </div>
      </Modal>
    </section>
  );
}

export default Results;
